// core/services/mediaService.ts — B6: upload an image to R2 via the backend.
// Returns the stored object key + its public URL. The caller submits the key
// through the normal resource endpoint (post-create, profile PATCH, community create).
import { api, ApiError } from '@/core/lib/apiClient';

// 'dm' (1.24.00) is a direct-message attachment — its own server-side size cap
// and storage prefix, but the same upload seam as everything else.
// 'poster' (video phase 2) is the one frame a video upload uses as its thumbnail.
export type MediaPurpose = 'avatar' | 'post' | 'banner' | 'cover' | 'dm' | 'poster';

/**
 * Upload result. `width`/`height` are the server-decoded intrinsic dimensions — pass them
 * back with the post so the feed can reserve the exact box and avoid layout shift. They are
 * optional because older backends did not return them.
 */
export interface UploadResult {
  key: string;
  url: string;
  width?: number;
  height?: number;
}

export const mediaService = {
  async upload(purpose: MediaPurpose, file: File): Promise<UploadResult> {
    const form = new FormData();
    form.append('purpose', purpose);
    form.append('file', file);
    return api<UploadResult>('/media/', { method: 'POST', body: form });
  },
} as const;

export function uploadErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    if (e.code === 'rate_limited')
      return "You're uploading too fast. Please wait a moment and try again.";
    if (e.code === 'file_too_large') {
      const mb = (e.fields as { limitMb?: number } | undefined)?.limitMb;
      return `Image too large${mb ? ` (max ${mb} MB)` : ''}.`;
    }
    if (e.code === 'unsupported_format') return 'Unsupported image format (use JPEG/PNG/WebP/GIF).';
    if (e.code === 'not_an_image') return 'That file is not a valid image.';
  }
  return fallback;
}
